import { useState, useMemo, useEffect } from 'react'
import classes from './MarketplaceCollections.module.scss'
import MarketplaceCollectionCard from '@/components/Sections/MarketplaceCollectionCard/MarketplaceCollectionCard'
import SortingOptions from '@/components/Sections/MarketplaceCollectionCard/SortingOptions/SortingOptions'
import SharedFilters from './SharedFilters'
import MobileFilterIcon from './Components/Icons/MobileFiltersIcon'
import { getBestSellerRanking } from '@/utils/getBestSellerRanking'

const MarketplaceCollections = ({ sections }) => {
  const [selectedCollection, setSelectedCollection] = useState(null)
  const [sortOption, setSortOption] = useState('-')
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const [priceRange, setPriceRange] = useState({ min: '', max: '' })
  const [filters, setFilters] = useState({
    onSale: false,
    valueBoxes: false
  })
  const [bestSellerRankings, setBestSellerRankings] = useState([])

  // Initial fetch of best seller rankings for all products
  useEffect(() => {
    const fetchInitialBestSellers = async () => {
      if (sections.length) {
        const allHandles = sections.flatMap(section =>
          section.mappedProducts?.map(product => product.handle) || []
        )
        const rankings = await getBestSellerRanking(allHandles)
        setBestSellerRankings(rankings)

        // Force resort by triggering sort change with same option
        setTimeout(() => {
          setSortOption(sortOption)
        }, 200)
      }
    }

    fetchInitialBestSellers()
  }, [sections])

  // Fetch best sellers when collection changes
  useEffect(() => {
    const fetchBestSellers = async () => {
      if (selectedCollection) {
        const currentCollection = sections.find(section => section.handle === selectedCollection)
        const currentHandles = currentCollection?.mappedProducts?.map(
          product => product.handle
        ) || []
        const rankings = await getBestSellerRanking(currentHandles)
        setBestSellerRankings(rankings)
      }
    }

    if (selectedCollection) {
      fetchBestSellers()
    }
  }, [selectedCollection, sections])

  // Watch for bestSellerRankings changes
  useEffect(() => {
    if (bestSellerRankings?.length > 0 && sortOption === '-') {
      // Force resort by triggering sort change with same option
      setSortOption(sortOption)
    }
  }, [bestSellerRankings])

  // Handle sort changes
  const handleSortChange = (event) => {
    const newSortOption = event.target.value || '-'
    setSortOption(newSortOption)
  }

  // Sort and filter products
  const sortProducts = (products) => {
    if (!products?.length) return [];

    // First apply filters
    let filtered = [...products];

    // Apply checkbox filters
    if (filters.onSale) {
      filtered = filtered.filter(product =>
        product.variants?.[0]?.compareAtPrice > product.variants?.[0]?.price
      );
    }

    if (filters.valueBoxes) {
      // Find the Value Boxes section
      const valueBoxSection = sections.find(section =>
        section.header?.headerText === 'Value Boxes'
      );

      // Get all products from the Value Boxes section
      if (valueBoxSection) {
        const valueBoxProducts = valueBoxSection.mappedProducts || [];
        filtered = filtered.filter(product =>
          valueBoxProducts.some(vbProduct =>
            vbProduct.content?.handle === product.content?.handle
          )
        );
      }
    }

    // Price range filter
    if (priceRange.min || priceRange.max) {
      filtered = filtered.filter(product => {
        const price = product.variants[0]?.price || 0;
        const meetsMin = !priceRange.min || price >= Number(priceRange.min);
        const meetsMax = !priceRange.max || price <= Number(priceRange.max);
        return meetsMin && meetsMax;
      });
    }

    // Create a new array with unique products based on handle
    const uniqueProducts = Array.from(new Map(
      filtered.map(product => [product.content?.handle, product])
    ).values());

    return [...uniqueProducts].sort((a, b) => {
      switch (sortOption) {
        case 'price-low':
          return (a.variants?.[0]?.price || 0) - (b.variants?.[0]?.price || 0)
        case 'price-high':
          return (b.variants?.[0]?.price || 0) - (a.variants?.[0]?.price || 0)
        default:
          return 0
      }
    })
  }

  // Get all products from all collections
  const allProducts = useMemo(() => {
    // Get unique products across all sections
    const products = sections.flatMap(section => section.mappedProducts || []);
    return Array.from(new Map(
      products.map(product => [product.content?.handle, product])
    ).values());
  }, [sections])

  // Update combinedFields to use allProducts directly
  const combinedFields = useMemo(() => ({
    backgroundColor: sections[0]?.backgroundColor,
    mappedProducts: sortProducts(allProducts),
    useFlyout: true,
    usePricing: true,
    useATC: false
  }), [allProducts, sections, sortOption, filters, priceRange])

  // Prepare selected collection with sorted products
  const selectedCollectionFields = useMemo(() => {
    if (!selectedCollection) return null;
    // Ensure unique products in the selected collection
    const uniqueProducts = Array.from(new Map(
      (selectedCollection.mappedProducts || []).map(product => [product.content?.handle, product])
    ).values());
    return {
      ...selectedCollection,
      mappedProducts: sortProducts(uniqueProducts)
    }
  }, [selectedCollection, sortOption, filters, priceRange]);

  // Memoize sorted products to prevent unnecessary re-renders
  const sortedProducts = useMemo(() => {
    if (!selectedCollectionFields?.mappedProducts && !allProducts.length) return [];
    return sortProducts(selectedCollectionFields?.mappedProducts || allProducts);
  }, [selectedCollectionFields, allProducts, sortOption, bestSellerRankings, filters, priceRange]);

  // Handle checkbox filters
  const handleFilterChange = (filterType) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }))
    setIsMobileFiltersOpen(false)
  }

  // Handle price range
  const handlePriceRangeChange = (newRange) => {
    setPriceRange(newRange)
    // Delay closing the mobile filter for price range changes
    setTimeout(() => {
      setIsMobileFiltersOpen(false)
    }, 1000) // 1.5 second delay
  }

  return (
    <div className={classes.marketplace}>
      {/* Desktop Sidebar */}
      <div className={classes.desktopSidebar}>
        <SharedFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          priceRange={priceRange}
          setPriceRange={handlePriceRangeChange}
        />

        <h2 className={classes.sidebarTitle}>Categories</h2>
        <div className={classes.sidebarLinks}>
          <button
            className={`${classes.sidebarLink} ${!selectedCollection ? classes.active : ''}`}
            onClick={() => setSelectedCollection(null)}
          >
            All Products
          </button>
          {sections.map(section => (
            <button
              key={section._key}
              className={`${classes.sidebarLink} ${selectedCollection?._key === section._key ? classes.active : ''}`}
              onClick={() => setSelectedCollection(section)}
            >
              {section.header?.headerText}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.content}>
        <div className={classes.contentHeader}>
          <div className={classes.contentControls}>
            <SortingOptions
              sortOption={sortOption}
              onSortChange={(value) => handleSortChange({ target: { value } })}
            />
            {/* Mobile Filter Toggle */}
            <button
              className={classes.mobileFilterButton}
              onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
            >
              <MobileFilterIcon />
            </button>
          </div>
        </div>

        {/* Mobile Filters Drawer */}
        <div className={`${classes.mobileFilters} ${isMobileFiltersOpen ? classes.open : ''}`}>
          <div className={classes.mobileFiltersHeader}>
            <h2>Filters</h2>
            <button onClick={() => setIsMobileFiltersOpen(false)}>×</button>
          </div>
          <div className={classes.mobileFiltersContent}>
            <SharedFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              priceRange={priceRange}
              setPriceRange={handlePriceRangeChange}
            />

            <h3 className={classes.filterTitle}>Categories</h3>
            <div className={classes.mobileFiltersContent}>
              <button
                className={`${classes.filterButton} ${!selectedCollection ? classes.active : ''}`}
                onClick={() => {
                  setSelectedCollection(null)
                  setIsMobileFiltersOpen(false)
                }}
              >
                All Products
              </button>
              {sections.map(section => (
                <button
                  key={section._key}
                  className={`${classes.filterButton} ${selectedCollection?._key === section._key ? classes.active : ''}`}
                  onClick={() => {
                    setSelectedCollection(section)
                    setIsMobileFiltersOpen(false)
                  }}
                >
                  {section.header?.headerText}
                </button>
              ))}
            </div>
          </div>
        </div>

        <MarketplaceCollectionCard
          fields={{
            ...selectedCollectionFields || combinedFields,
            mappedProducts: sortedProducts
          }}
        />
      </div>
    </div>
  )
}

export default MarketplaceCollections